import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Keyboard } from 'lucide-react';
import { POS_SHORTCUTS, formatShortcut } from '../hooks/useKeyboardShortcuts';

interface ShortcutsHelpModalProps {
  open: boolean;
  onClose: () => void;
}

export const ShortcutsHelpModal = ({ open, onClose }: ShortcutsHelpModalProps) => {
  const shortcutGroups = [
    {
      title: 'Recherche / Search',
      shortcuts: [
        { ...POS_SHORTCUTS.SEARCH, label: 'Rechercher produit' },
        { ...POS_SHORTCUTS.BARCODE, label: 'Scanner code-barre' },
      ],
    },
    {
      title: 'Panier / Cart',
      shortcuts: [
        { ...POS_SHORTCUTS.CHECKOUT, label: 'Encaisser' },
        { ...POS_SHORTCUTS.CLEAR_CART, label: 'Vider panier' },
        { ...POS_SHORTCUTS.INCREASE_QTY, label: 'Augmenter quantité' },
        { ...POS_SHORTCUTS.DECREASE_QTY, label: 'Diminuer quantité' },
      ],
    },
    {
      title: 'Ventes en attente / Held Sales',
      shortcuts: [
        { ...POS_SHORTCUTS.HOLD_SALE, label: 'Mettre en attente' },
        { ...POS_SHORTCUTS.RETRIEVE_SALE, label: 'Récupérer vente' },
      ],
    },
    {
      title: 'Paiement / Payment',
      shortcuts: [
        { ...POS_SHORTCUTS.CASH_PAYMENT, label: 'Paiement espèces' },
        { ...POS_SHORTCUTS.CARD_PAYMENT, label: 'Paiement carte' },
      ],
    },
    {
      title: 'Navigation',
      shortcuts: [
        { ...POS_SHORTCUTS.NEXT_CATEGORY, label: 'Catégorie suivante' },
        { ...POS_SHORTCUTS.PREV_CATEGORY, label: 'Catégorie précédente' },
      ],
    },
    {
      title: 'Autre / Other',
      shortcuts: [
        { ...POS_SHORTCUTS.SELECT_CUSTOMER, label: 'Sélectionner client' },
        { ...POS_SHORTCUTS.CLOSE_SESSION, label: 'Fermer session' },
        { ...POS_SHORTCUTS.SHOW_SHORTCUTS, label: 'Afficher raccourcis' },
      ],
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5" />
            Raccourcis clavier / Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {shortcutGroups.map((group) => (
            <div key={group.title}>
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">
                {group.title}
              </h3>
              <div className="space-y-2">
                {group.shortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600">{shortcut.label}</span>
                    <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
                      {formatShortcut(shortcut)}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-900">
            💡 <strong>Astuce:</strong> La plupart des raccourcis fonctionnent également
            quand vous tapez dans le champ de recherche. Appuyez sur <kbd className="px-1 py-0.5 bg-white border border-blue-300 rounded text-xs">Esc</kbd> pour fermer
            les modales et revenir au POS.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
